// Last comparison cache: local-only storage for restoring a useful view when
// the app starts offline. The schema is intentionally narrow and versioned.

export const LAST_COMPARISON_KEY = 'gar_last_comparison';
const SCHEMA_VERSION = 1;

const APP_FIELDS = [
  'trackId',
  'trackName',
  'artworkUrl60',
  'artworkUrl100',
  'bundleId',
  'primaryGenreId',
  'primaryGenreName',
];

const ROW_FIELDS = [
  'countryCode',
  'countryName',
  'available',
  'trackId',
  'name',
  'price',
  'formattedPrice',
  'currency',
  'averageUserRating',
  'userRatingCount',
  'storeUrl',
  'error',
  'fileSize',
  'contentRating',
  'minimumOsVersion',
  'languageCodesISO2A',
  'languageCount',
  'releaseDate',
  'version',
  'releaseNotes',
  'description',
  'chartLabel',
  'chartUnavailable',
  'chartRankFree',
  'chartRankPaid',
  'chartRankGrossing',
  'popularityScore',
  'popularityRank',
  'popularityDelta',
];

function getStore() {
  return globalThis.localStorage ?? null;
}

function pickFields(source, fields) {
  const out = {};
  for (const field of fields) {
    if (source[field] !== undefined) out[field] = source[field];
  }
  return out;
}

function normalizeApp(app) {
  if (!app || typeof app !== 'object' || app.trackId === undefined || app.trackId === null) {
    return null;
  }
  const normalized = pickFields(app, APP_FIELDS);
  normalized.trackId = String(app.trackId);
  normalized.trackName = typeof app.trackName === 'string' ? app.trackName : '';
  return normalized;
}

function normalizeRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const normalized = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object' || typeof row.countryCode !== 'string') continue;
    normalized.push(pickFields(row, ROW_FIELDS));
  }
  return normalized.length > 0 ? normalized : null;
}

function isValidCachedComparison(value) {
  return Boolean(
    value &&
      typeof value === 'object' &&
      value.version === SCHEMA_VERSION &&
      typeof value.cachedAt === 'string' &&
      value.app &&
      typeof value.app === 'object' &&
      value.app.trackId &&
      Array.isArray(value.rows) &&
      value.rows.length > 0,
  );
}

/**
 * Serialize the visible comparison into a versioned JSON payload.
 * @param {object} app
 * @param {object[]} rows
 * @param {string} [cachedAt]
 * @returns {string|null}
 */
export function serializeCachedComparison(app, rows, cachedAt = new Date().toISOString()) {
  const normalizedApp = normalizeApp(app);
  const normalizedRows = normalizeRows(rows);
  if (!normalizedApp || !normalizedRows || typeof cachedAt !== 'string' || !cachedAt) {
    return null;
  }
  return JSON.stringify({
    version: SCHEMA_VERSION,
    cachedAt,
    app: normalizedApp,
    rows: normalizedRows,
  });
}

/**
 * Parse a cached comparison. Corrupt, old, or incomplete payloads return null.
 * @param {string|null|undefined} raw
 * @returns {{ version: number, cachedAt: string, app: object, rows: object[] }|null}
 */
export function deserializeCachedComparison(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    return isValidCachedComparison(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Store the last successfully rendered comparison in localStorage.
 * @param {object} app
 * @param {object[]} rows
 * @param {Storage} [store]
 * @param {string} [cachedAt]
 */
export function saveLastComparison(app, rows, store = getStore(), cachedAt = new Date().toISOString()) {
  if (!store) return;
  const serialized = serializeCachedComparison(app, rows, cachedAt);
  if (!serialized) return;
  try {
    store.setItem(LAST_COMPARISON_KEY, serialized);
  } catch {
    // Storage may be unavailable or full; offline restore is best-effort only.
  }
}

/**
 * Read the last cached comparison.
 * @param {Storage} [store]
 * @returns {{ version: number, cachedAt: string, app: object, rows: object[] }|null}
 */
export function loadLastComparison(store = getStore()) {
  if (!store) return null;
  try {
    return deserializeCachedComparison(store.getItem(LAST_COMPARISON_KEY));
  } catch {
    return null;
  }
}

/**
 * Return a cached comparison only for an offline startup.
 * @param {{ store?: Storage, online?: boolean }} [options]
 * @returns {{ version: number, cachedAt: string, app: object, rows: object[] }|null}
 */
export function loadOfflineComparison(options = {}) {
  const online = options.online ?? (typeof navigator === 'undefined' ? true : navigator.onLine !== false);
  if (online) return null;
  return loadLastComparison(options.store);
}
